import React, { useState } from 'react';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import useSettings from '../../hooks/useSettings';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import { IconButton, Tooltip } from '@mui/material';
import apiClient from '../../config/apiClient';
import { toast } from 'react-toastify';
import Loading from '../../components/Loading';


interface HistoricalReportTableProps {
  selectedTenant:string;
  reports: {
    reportId: number;
    tenantName: string;
    reportDate: string;
    SharedMailbox: number;
    UserMailbox: number;
    RoomMailbox: number;
    ServiceMailbox: number;
    OutOfScope: number;
    ADMAccount: number;
    scopeChangeStatus: string;
    changePercentage: number;
    total: number;
    threshold: number;
  }[];
}


const HistoricalReportTable: React.FC<HistoricalReportTableProps> = ({ reports,selectedTenant }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  
  const handleExcelUsers = async (reportId: number) => {
    if (selectedTenant) {
      setLoading(true);
      try {

        const response = await apiClient.get(
          `/usersReport/${selectedTenant}/${reportId}`,
          { responseType: 'blob' }
        );

        const contentDisposition = response.headers['content-disposition'];
        const fileNameMatch = contentDisposition?.match(/filename="(.+)"/);
        const fileName = fileNameMatch ? fileNameMatch[1] : 'report.xlsx';
        console.log('header', contentDisposition);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success('Excel downloaded successfully!', { position: 'top-right' });
      } catch (err) {
        setError('Error generating historical report');
        toast.error('Failed to download Excel report!', { position: 'top-right' });
      } finally {
        setLoading(false);
      }
    } else {
      toast.warn('Please select a tenant and date range.', { position: 'top-right' });
    }
  };
  const { themeMode } = useSettings();
  if (loading) return <Loading />;
  if (error) return <p>{error}</p>;

  return (
    <table className="min-w-full border border-gray-200 text-center">
      <thead>
        <tr>
          <th className="px-4 py-2 border-b text-left">Report ID</th>
          <th className="px-4 py-2 border-b text-left">Tenant Name</th>
          <th className="px-4 py-2 border-b text-left">Date</th>
          <th className="px-4 py-2 border-b">Shared Mailbox</th>
          <th className="px-4 py-2 border-b">User Mailbox</th>
          <th className="px-4 py-2 border-b">Room Mailbox</th>
          <th className="px-4 py-2 border-b">Service Account</th>
          <th className="px-4 py-2 border-b">Out of Scope</th>
          <th className="px-4 py-2 border-b">ADM Account</th>
          <th className="px-4 py-2 border-b">Total</th>
          <th className="px-4 py-2 border-b">Threshold</th>
          <th className="px-4 py-2 border-b text-left">Scope Change Status</th>
          <th className="px-4 py-2 border-b">Change Percentage</th>
        </tr>
      </thead>
      <tbody>
        {reports.map((report) => {
          const rowBackground =
          report.scopeChangeStatus === 'increase'
            ? themeMode === 'dark'
              ? 'bg-green-900'
              : 'bg-green-100'
            : report.scopeChangeStatus === 'decrease'
            ? themeMode === 'dark'
              ? 'bg-red-900'
              : 'bg-red-100'
            : themeMode === 'dark'
            ? 'bg-gray-800'
            : 'bg-white';

          return (
            <tr key={report.reportId} className={rowBackground}>
              <td className="px-4 py-2 border-b text-left">{report.reportId}</td>
              <td className="px-4 py-2 border-b text-left">{report.tenantName}</td>
              <td className="px-4 py-2 border-b text-left">{new Date(report.reportDate).toLocaleDateString()}</td>
              <td className="px-4 py-2 border-b">{report.SharedMailbox}</td>
              <td className="px-4 py-2 border-b">{report.UserMailbox}</td>
              <td className="px-4 py-2 border-b">{report.RoomMailbox}</td>
              <td className="px-4 py-2 border-b">{report.ServiceMailbox}</td>
              <td className="px-4 py-2 border-b">{report.OutOfScope}</td>
              <td className="px-4 py-2 border-b">{report.ADMAccount}</td>
              <td className="px-4 py-2 border-b">{report.total}</td>
              <td className="px-4 py-2 border-b">{report.threshold}</td>
              <td className="px-4 py-2 border-b text-left">
                {report.scopeChangeStatus === 'increase' ? (
                  <span className="flex items-center space-x-2">
                    <ArrowUpwardIcon style={{ color: 'green' }} />
                    <span>Increase</span>
                  </span>
                ) : report.scopeChangeStatus === 'decrease' ? (
                  <span className="flex items-center space-x-2">
                    <ArrowDownwardIcon style={{ color: 'red' }} />
                    <span>Decrease</span>
                  </span>
                ) : (
                  <span>Unchanged</span>
                )}
              </td>
              <td className="px-4 py-2 border-b">{report.changePercentage !== null ? `${report.changePercentage.toFixed(0)}%` : 'N/A'}</td>
              <td className="px-4 py-2 border-b">
                <Tooltip title="Download Users details" placement="top" arrow>
                  <IconButton onClick={() => handleExcelUsers(report.reportId)}>
                    <ContactPageIcon />
                  </IconButton>
                </Tooltip>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default HistoricalReportTable;
